import React, { forwardRef, useCallback, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AskQuestionDrawer from '../AskQuestionDrawer/AskQuestionDrawer.component';
import {
  Question,
  QuestionManagingDrawerCommonProps,
} from 'router/subrouters/DataRoom/models/QAndA';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { Button } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';

type EditQuestionDrawerProps = QuestionManagingDrawerCommonProps;

const EditQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  EditQuestionDrawerProps
> = ({ question, onClose, onQuestionsUpdate, updateQuestion }, ref) => {
  const { t } = useTranslation();
  const { isQuestionDeletableForUser, deleteQuestionCallback } =
    useContext(QAndAContext);

  const {
    _id,
    dataRoomNodeId,
    subject,
    otherSubject,
    priority,
    content,
    files,
  } = question;

  const initialValues = {
    dataRoomNodeId,
    subject,
    otherSubject,
    priority,
    content,
  };

  const onQuestionSubmit = useCallback(
    async (values: Partial<Question>, node: string) => {
      await dataRoomApi.questionsAndAnswers.editQuestion(
        question?._id,
        values,
        node,
      );
      onQuestionsUpdate?.();
    },
    [onQuestionsUpdate, question?._id],
  );

  const DeleteButton = useMemo(
    () =>
      isQuestionDeletableForUser(question) ? (
        <Button
          className="delete"
          icon={TrashIcon}
          onClick={async (ev) => {
            (ev.target as any).classList.add('is-loading');
            await deleteQuestionCallback(question);
            onQuestionsUpdate?.();
            onClose?.();
            (ev.target as any).classList.remove('is-loading');
          }}
        >
          {t('deleteQuestion')}
        </Button>
      ) : null,
    [
      deleteQuestionCallback,
      isQuestionDeletableForUser,
      onClose,
      onQuestionsUpdate,
      question,
      t,
    ],
  );

  const onRemoveQuestionFile = useCallback(
    (fileId: string) => {
      const newQ = {
        ...question,
        files: files.filter(({ _id }) => _id !== fileId),
      };
      updateQuestion(newQ);
    },
    [files, question, updateQuestion],
  );

  return (
    <AskQuestionDrawer
      title={t(question.rejectedBy ? 'rejectedQuestion' : 'editAQuestion')}
      selectedForQAndADrawer={question.dataRoomNode}
      initialValues={initialValues}
      questionId={_id}
      files={files}
      ref={ref}
      deleteButton={DeleteButton}
      rejectedBySide={question.rejectedBy}
      rejectQuestionRespond={question.rejectQuestionRespond}
      onSubmit={onQuestionSubmit}
      onClose={onClose}
      onRemoveQuestionFile={onRemoveQuestionFile}
    />
  );
};

export default forwardRef(EditQuestionDrawer);
